/**
 * Booking Service
 * Provides booking operations using the GraphQL API
 * This is a compatibility layer that delegates to the GraphQL bookingService
 */

import {
  getAllBookings,
  getBookingsByServiceProviderId,
  updateBookingStatus as graphqlUpdateStatus,
  type BookingRecord
} from './graphql/bookingService';

/**
 * Interface for booking data
 */
export interface Booking {
  id: string;
  passenger_name: string;
  passenger_email: string;
  passenger_mobile?: string;
  selected_ride: string;
  serviceprovider_id: string;
  pickup_date: string;
  pickup_time: string;
  pickup_type: 'asap' | 'scheduled';
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  booking_details?: string;
  created_at: string;
  pickup_location: string;
  dropoff_location: string;
  booking_number?: string;
  service_provider?: {
    serviceprovider_name: string;
    id: string;
  };
}

/**
 * Convert BookingRecord to Booking format
 */
function toBooking(record: BookingRecord): Booking {
  return {
    id: record.id,
    passenger_name: record.passenger_name,
    passenger_email: record.passenger_email,
    passenger_mobile: record.passenger_mobile,
    selected_ride: record.selected_ride || '',
    serviceprovider_id: record.serviceprovider_id || '',
    pickup_date: record.pickup_date,
    pickup_time: record.pickup_time,
    pickup_type: record.pickup_type,
    status: record.status as Booking['status'],
    booking_details: record.booking_details,
    created_at: record.$createdAt || '',
    pickup_location: record.pickup_location,
    dropoff_location: record.dropoff_location,
    booking_number: record.booking_number,
    // Map flat service_provider_name field to nested service_provider object
    service_provider: record.service_provider_name ? {
      id: record.serviceprovider_id || '',
      serviceprovider_name: record.service_provider_name
    } : undefined
  };
}

/**
 * Updates the status of a booking in the database
 * @param bookingId - The ID of the booking to update
 * @param newStatus - The new status to set
 * @returns Object containing success flag and optional error message
 */
export const updateBookingStatus = async (
  bookingId: string,
  newStatus: 'pending' | 'confirmed' | 'completed' | 'cancelled',
  reason?: string
): Promise<{ success: boolean; error?: string }> => {
  console.log('[BookingService] updateBookingStatus called', { bookingId, newStatus, reason });
  try {
    const { error } = await graphqlUpdateStatus(bookingId, newStatus, reason);

    if (error) {
      console.error('[BookingService] Error updating booking status:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred while updating booking status'
      };
    }

    console.log('[BookingService] Booking status updated successfully for', bookingId);
    return { success: true };
  } catch (err: any) {
    console.error('[BookingService] Exception updating booking status:', err);
    return {
      success: false,
      error: err.message || 'An unexpected error occurred while updating booking status'
    };
  }
};

/**
 * Fetches bookings with related service provider information
 * @param filters - Optional filters to apply to the query
 * @returns Array of bookings with service provider data
 */
export const fetchBookings = async (
  filters?: {
    serviceproviderID?: string;
    status?: string;
    userID?: string;
  }
): Promise<{ data: Booking[] | null; error: Error | null }> => {
  try {
    console.log('[BookingService] fetchBookings called', { filters });

    let result: { data: BookingRecord[] | null; error: Error | null };

    // Use service provider specific query if provided
    if (filters?.serviceproviderID) {
      result = await getBookingsByServiceProviderId(filters.serviceproviderID);
    } else {
      result = await getAllBookings();
    }

    if (result.error) {
      return { data: null, error: result.error };
    }

    if (!result.data || result.data.length === 0) {
      console.log('[BookingService] No bookings found');
      return { data: [], error: null };
    }

    // Convert to Booking format and apply client-side filters
    let bookings = result.data.map(toBooking);

    // Apply status filter client-side if needed
    if (filters?.status && filters.status !== 'all') {
      bookings = bookings.filter(b => b.status === filters.status);
    }

    // Apply user filter client-side if needed
    // Note: This assumes the GraphQL query returns all bookings
    // In production, you'd want server-side filtering

    console.log('[BookingService] Bookings fetched successfully', { count: bookings.length });
    return { data: bookings, error: null };
  } catch (err: any) {
    console.error('Error in fetchBookings:', err);
    return { data: null, error: err };
  }
};
